import React, { ReactNode, useEffect } from 'react';
import { PropsWithChildren } from 'react';
import { cx } from 'class-variance-authority';
import { AppSidebar } from '@/components/app-sidebar';
import { ChevronLeft, X } from 'lucide-react';
import { Link } from 'react-router';
import { cn } from '@/lib/utils';
import { useBreakpoint } from '@/hooks/use-breakpoint';
import { useIsMobile } from '@/hooks/use-mobile';
import { AnnouncementBar } from '@/components/announcement-bar';
import { AppUnderConstructionBar } from '@/components/app-under-construction-bar';
import { useChatwootWidget } from '@/components/chatwoot-widget/chatwoot-widget-context';
import { useI18nContext } from '@/i18n/i18n-react';
import { mobileLayer } from '@/lib/mobile-layer';

/**
 * Full-screen task (composer, editors). Below 768px: no sidebar and no tab
 * bar, only the header with a back target (SHELL-03 AC 9).
 */
export function CampaignLayout({ children }: PropsWithChildren) {
  const shouldShowRightContent = useBreakpoint(1200);
  const isMobile = useIsMobile();
  const { setHasShellOnScreen } = useChatwootWidget();

  // On desktop the AppSidebar registers itself; on mobile the task screen is
  // the shell on screen (the Help FAB stays hidden).
  useEffect(() => {
    if (!isMobile) return;
    setHasShellOnScreen(true);
    return () => setHasShellOnScreen(false);
  }, [isMobile, setHasShellOnScreen]);

  const hasRightContent = React.Children.map(
    children,
    (c) =>
      c &&
      typeof c === 'object' &&
      'type' in c &&
      c.type === CampaignRightContent
  )?.some((x) => x);

  return (
    <div className="flex h-screen w-full flex-col max-md:h-dvh">
      <AnnouncementBar />
      <AppUnderConstructionBar />
      <div className="flex min-h-0 flex-1">
        {!isMobile && <AppSidebar />}
        <main
          className={cx(
            'min-w-0 flex-1 w-full h-full max-h-full grid grid-rows-[auto_1fr_auto]',
            !shouldShowRightContent || !hasRightContent
              ? 'grid-cols-[minmax(0,1fr)]'
              : // The right column (composer's Korza Studio) grows with wide
                // screens instead of leaving the space empty.
                'grid-cols-[minmax(0,1fr)_clamp(400px,34vw,600px)]'
          )}
        >
          {children}
        </main>
      </div>
    </div>
  );
}

type CampaignHeaderProps = {
  title: string | ReactNode;
  className?: string;
  full?: boolean;
  /** Shown to the right of the title (e.g. the editor's "Publicar"). */
  actions?: ReactNode;
  /** `back` (ChevronLeft, "Voltar", default) or `close` (X, "Fechar"). */
  backVariant?: 'back' | 'close';
} & (
  | { backTo: string; leading?: never }
  /** Extended by Fase 3 (mobile-push E1): replaces the back link. */
  | { leading: ReactNode; backTo?: never }
);

export function CampaignHeader({
  title,
  backTo,
  leading,
  className,
  actions,
  backVariant = 'back',
}: CampaignHeaderProps) {
  const { LL } = useI18nContext();

  return (
    <div
      className={cx(
        'flex items-center px-6 row-start-1 col-start-1 space-x-4 border-b border-border h-[69px]',
        className
      )}
    >
      {leading ?? (
        // SPEC_DEVIATION: `max-md:` touch target instead of bare
        // `min-h-touch min-w-touch`.
        // Reason: the header also renders on the desktop (AD-016).
        <Link
          to={backTo ?? '../'}
          aria-label={
            backVariant === 'close' ? LL.shell.close() : LL.shell.back()
          }
          className={cn(
            'max-md:flex max-md:items-center max-md:justify-center',
            mobileLayer.touch
          )}
        >
          {backVariant === 'close' ? <X /> : <ChevronLeft />}
        </Link>
      )}
      <div className="min-w-0 w-full truncate text-2xl md:text-3xl font-bold tracking-tight">
        {title}
      </div>
      {actions}
    </div>
  );
}

export function CampaignMainContent({
  children,
  className,
}: PropsWithChildren<{ className?: string }>) {
  return (
    <div className={cn('p-6 row-start-2 col-start-1 overflow-auto', className)}>
      {children}
    </div>
  );
}

export function CampaignRightContent({ children }: PropsWithChildren) {
  const shouldShowRightContent = useBreakpoint(1200);
  if (!shouldShowRightContent) {
    return null; // Hide on mobile
  }
  return <div className="row-start-1 col-start-2 row-end-3">{children}</div>;
}

export function CampaignFooter({
  children,
  className,
}: PropsWithChildren<{ className?: string }>) {
  return (
    <footer
      className={cx(
        'bg-background border-t px-6 py-4 flex justify-end gap-4 row-start-3 col-span-2',
        className
      )}
    >
      {children}
    </footer>
  );
}
